import React, { useEffect, useRef, useState } from 'react'
import Icon from './icon'
import { useClickOutside } from './util'
import Input from './input'
import { buttonClassName } from './button/utils'

export type SelectItemProps = {
  id: string | number
} & object

type SelectProps<T extends SelectItemProps> = {
  name: string
  items: T[]
  selectedItems: T[]
  color?: 'primary' | 'secondary'
  invert?: boolean
  itemComponent: React.FC<T>
  searchable?: boolean
} & (
  | {
      multi: true
      // eslint-disable-next-line no-unused-vars
      onSelect?: (val: T[]) => void
    }
  | {
      multi: false
      // eslint-disable-next-line no-unused-vars
      onSelect?: (val: T | undefined) => void
    }
)
const Select = <T extends SelectItemProps>({
  name,
  items,
  selectedItems,
  color = 'primary',
  invert = false,
  multi,
  onSelect,
  itemComponent: ItemComponent,
  searchable = false,
}: SelectProps<T>) => {
  const [isDisplay, setIsDisplay] = useState(false)

  const wrapperRef = useRef<HTMLDivElement>(null)
  useClickOutside(wrapperRef, () => {
    setIsDisplay(false)
  })

  const [currentSelection, setCurrentSelection] = useState<Record<string, T>>(
    selectedItems.reduce((prev, cur) => ({ ...prev, [cur.id]: cur }), {}),
  )

  useEffect(() => {
    setCurrentSelection(
      selectedItems.reduce((prev, cur) => ({ ...prev, [cur.id]: cur }), {}),
    )
  }, [selectedItems])

  const handleOnSelect = (val: Record<string, T>) => {
    if (multi && onSelect) onSelect(Object.values(val))
    if (!multi && onSelect)
      onSelect(Object.keys(val).length > 0 ? Object.values(val)[0] : undefined)
  }

  return (
    <div ref={wrapperRef} className="relative flex flex-col">
      <button
        type="button"
        onClick={() => setIsDisplay(!isDisplay)}
        className={buttonClassName({ color, invert })}
      >
        <div className="flex flex-row items-center gap-4 text-sm">
          {name} <Icon icon="caret" className="w-3 h-3" />
        </div>
      </button>

      {isDisplay && items.length > 0 && (
        <div className="absolute z-10 flex flex-col items-stretch justify-center gap-2 py-4 overflow-y-auto bg-white whitespace-nowrap rounded-xl top-12">
          {searchable && <Input placeholder="Search" />}
          {items.map((item) => (
            <button
              type="button"
              key={item.id}
              className="flex flex-row items-center gap-2 px-2 text-sm text-left hover:bg-primary-50 lg:px-4 lg:py-1 focus:outline-none"
              onClick={() => {
                if (multi) {
                  const newItems = { ...currentSelection, [item.id]: item }
                  if (currentSelection[item.id]) {
                    delete newItems[item.id]
                  }
                  setCurrentSelection(newItems)
                  handleOnSelect(newItems)
                } else {
                  setCurrentSelection({ [item.id]: item })
                  handleOnSelect({ [item.id]: item })
                }
              }}
            >
              {multi && (
                <input
                  type="checkbox"
                  readOnly
                  checked={!!currentSelection[item.id]}
                  className="rounded-md focus:outline-none hover:outline-none active:outline-none checked:outline-none text-secondary border-secondary"
                />
              )}
              <ItemComponent {...item} />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default Select
